import { useState } from 'react'
import { Card, Box, Typography, List, useTheme } from '@mui/material'
import { SwitchTenantCardProps } from './SwitchTenantCard.types'
import { Button } from './Button'

export const SwitchTenantCard = ({
  title,
  tenantList,
  initialTenantId = '',
  primaryButtonLabel,
  secondaryButtonLabel,
  showSecondaryButton = true,
  handlePrimaryButtonClick,
  handleSecondaryButtonClick,
}: SwitchTenantCardProps) => {
  const [selectedTenantId, setSelectedTenantId] =
    useState<string>(initialTenantId)
  const theme = useTheme()

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLElement>,
    tenantId: string
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setSelectedTenantId(tenantId)
    }
  }

  const shouldDisableButton = () => {
    return !selectedTenantId || selectedTenantId === initialTenantId
  }

  return (
    <Card>
      <Box
        sx={{
          paddingTop: '18px',
          paddingBottom: '18px',
          paddingLeft: '36px',
          borderBottom: `1px solid ${theme.palette.secondary.main}`,
        }}
      >
        <Typography variant="h2" color="primary">
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          padding: '36px 36px 0 36px',
        }}
      >
        <List
          component="nav"
          sx={{
            maxHeight: '60vh',
            overflowY: 'auto',
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
          }}
          aria-label="list of tenants"
        >
          {tenantList.map((tenant, index) => {
            const isSelected = tenant.id === selectedTenantId
            const isOdd = index % 2 !== 0

            return (
              <Box
                tabIndex={0}
                key={tenant.id}
                onClick={() => setSelectedTenantId(tenant.id)}
                onKeyDown={(e) => handleKeyDown(e, tenant.id)}
                role="button"
                aria-label={tenant.name}
                sx={{
                  padding: '20px 24px',
                  borderTop: `1px solid ${theme.palette.grey[300]}`,
                  cursor: 'pointer',
                  backgroundColor: isSelected
                    ? theme.palette.secondary.main
                    : isOdd
                      ? theme.palette.grey[50]
                      : 'transparent',
                  color: isSelected ? theme.palette.common.white : 'inherit',
                }}
              >
                <Typography variant="body1">{tenant.name}</Typography>
              </Box>
            )
          })}
        </List>
      </Box>
      <Box
        sx={{
          padding: '48px 36px',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Box visibility={showSecondaryButton ? 'visible' : 'hidden'}>
          <Button
            variant="outlined"
            sx={{ marginRight: '20px' }}
            onClick={() => handleSecondaryButtonClick()}
          >
            {secondaryButtonLabel}
          </Button>
        </Box>
        <Button
          variant="contained"
          disabled={shouldDisableButton()}
          onClick={() => handlePrimaryButtonClick(selectedTenantId)}
        >
          {primaryButtonLabel}
        </Button>
      </Box>
    </Card>
  )
}
